// ═══════════════ COMPLIANCE — CLIENT-SIDE SERVICE (Batch 10) ═══════════════
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// ─── Incidents (Notifiable Occurrences) ───
export async function reportIncident(data) {
  const fn = httpsCallable(functions, 'reportIncident');
  return (await fn(data)).data;
}

export async function addIncidentInvestigationNote(incidentId, note) {
  const fn = httpsCallable(functions, 'addIncidentInvestigationNote');
  return (await fn({ incidentId, note })).data;
}

export async function updateIncidentStatus(incidentId, status, metadata) {
  const fn = httpsCallable(functions, 'updateIncidentStatus');
  return (await fn({ incidentId, status, metadata })).data;
}

export async function getIncidents(filters = {}) {
  const fn = httpsCallable(functions, 'getIncidents');
  return (await fn({ filters })).data;
}

// ─── Complaints ───
export async function submitComplaint(data) {
  const fn = httpsCallable(functions, 'submitComplaint');
  return (await fn(data)).data;
}

export async function acknowledgeComplaint(complaintId) {
  const fn = httpsCallable(functions, 'acknowledgeComplaint');
  return (await fn({ complaintId })).data;
}

export async function addComplaintCorrespondence(complaintId, direction, body) {
  const fn = httpsCallable(functions, 'addComplaintCorrespondence');
  return (await fn({ complaintId, direction, body })).data;
}

export async function resolveComplaint(complaintId, resolution) {
  const fn = httpsCallable(functions, 'resolveComplaint');
  return (await fn({ complaintId, resolution })).data;
}

export async function escalateComplaintToIncident(complaintId, incidentType) {
  const fn = httpsCallable(functions, 'escalateComplaintToIncident');
  return (await fn({ complaintId, incidentType })).data;
}

export async function getComplaints(filters = {}) {
  const fn = httpsCallable(functions, 'getComplaints');
  return (await fn({ filters })).data;
}

export async function getMyComplaints() {
  const fn = httpsCallable(functions, 'getMyComplaints');
  return (await fn({})).data;
}

// ─── Driver Consents ───
export async function recordDriverConsent(consentType, wordingVersion) {
  const fn = httpsCallable(functions, 'recordDriverConsent');
  return (await fn({ consentType, wordingVersion })).data;
}

export async function getDriverConsents(driverId) {
  const fn = httpsCallable(functions, 'getDriverConsents');
  return (await fn({ driverId })).data;
}

// ─── Admin Alerts ───
export async function getAdminAlerts(filters = {}) {
  const fn = httpsCallable(functions, 'getAdminAlerts');
  return (await fn({ filters })).data;
}

export async function acknowledgeAdminAlert(alertId) {
  const fn = httpsCallable(functions, 'acknowledgeAdminAlert');
  return (await fn({ alertId })).data;
}

// ─── Config ───
let _complianceConfigCache = null;
export async function getComplianceConfig() {
  if (_complianceConfigCache) return _complianceConfigCache;
  const fn = httpsCallable(functions, 'getComplianceConfig');
  _complianceConfigCache = (await fn({})).data;
  return _complianceConfigCache;
}

// ─── Helper: NSW driver licence number sanity check ───
// NSW does not issue drivers a standalone "PT number". A driver's point-to-point
// credential is the PT Code (Passenger Transport Licence Code) applied to their
// existing NSW driver licence via Service NSW. The authoritative verification is
// the admin checking the Driver Vehicle Dashboard (DVD) at review time — not a
// string format. This is only a light typo-guard on the licence number.
export function validateLicenceNumberFormat(licenceNo) {
  if (typeof licenceNo !== 'string') return false;
  const t = licenceNo.trim();
  return t.length >= 6 && t.length <= 10;
}

// ─── Tax invoices and driver tax summaries (Batch 10.5) ───
export async function getRiderTaxInvoice(bookingId) {
  const fn = httpsCallable(functions, 'getRiderTaxInvoice');
  return (await fn({ bookingId })).data;
}

export async function regenerateRiderTaxInvoice(bookingId) {
  const fn = httpsCallable(functions, 'regenerateRiderTaxInvoice');
  return (await fn({ bookingId })).data;
}

// period: 'YYYY-MM', 'YYYY-Q[1-4]', or 'YYYY'
export async function getDriverTaxSummary(period, driverId = null) {
  const fn = httpsCallable(functions, 'getDriverTaxSummary');
  return (await fn({ period, driverId })).data;
}

// ─── Helper: trigger download from signed URL ───
export function downloadFromSignedUrl(url, suggestedFilename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = suggestedFilename || 'download';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}


// ─── Helper: severity badge styling ───
export function getSeverityBadge(severity) {
  switch (severity) {
    case 'critical': return { bg: '#fee2e2', color: '#991b1b', label: 'Critical' };
    case 'high':     return { bg: '#fed7aa', color: '#9a3412', label: 'High' };
    case 'medium':   return { bg: '#fef3c7', color: '#92400e', label: 'Medium' };
    case 'low':      return { bg: '#dbeafe', color: '#1e40af', label: 'Low' };
    default:         return { bg: '#f1f5f9', color: '#5a6578', label: severity };
  }
}

// ─── Levy reports (Batch 10.5) ───
export async function generateLevyReport(monthKey) {
  const fn = httpsCallable(functions, 'generateLevyReport');
  return (await fn({ monthKey })).data;
}

export async function markLevyReportSubmitted(monthKey, submissionNotes) {
  const fn = httpsCallable(functions, 'markLevyReportSubmitted');
  return (await fn({ monthKey, submissionNotes })).data;
}

export async function acknowledgeLevyReport(monthKey, commissionerReceiptNumber) {
  const fn = httpsCallable(functions, 'acknowledgeLevyReport');
  return (await fn({ monthKey, commissionerReceiptNumber })).data;
}

export async function listLevyReports(filters = {}) {
  const fn = httpsCallable(functions, 'listLevyReports');
  return (await fn({ filters })).data;
}

export async function getLevyReport(monthKey) {
  const fn = httpsCallable(functions, 'getLevyReport');
  return (await fn({ monthKey })).data;
}

export async function markLevyCsvExported(monthKey) {
  const fn = httpsCallable(functions, 'markLevyCsvExported');
  return (await fn({ monthKey })).data;
}

// ─── CSV export helper (client-side, generates and triggers download) ───
// [VERIFY: NSW Point to Point Commissioner — confirm exact column order, headers, and date format]
export function exportLevyReportToCsv(report) {
  if (!report || !Array.isArray(report.trips)) {
    throw new Error('Report has no trips to export');
  }

  // Header row — [VERIFY] against NSW spec
  const headers = [
    'Trip Reference',
    'Completion Date',
    'Completion Time',
    'Vehicle Plate',
    'Pickup Suburb',
    'Destination Suburb',
    'Fare Excl Levy (AUD)',
    'Levy Amount (AUD)',
    'GST (AUD)',
    'Total Fare (AUD)',
    'Payment Status',
    'Levy Status',
    'Series ID',
    'Vehicle Group ID',
  ];

  // Format ISO timestamp to date + time strings
  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  };
  const fmtTime = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[1].substring(0, 8);
  };
  // Extract suburb from address (best-effort — NSW Commissioner may need full address)
  const extractSuburb = (addr) => {
    if (!addr) return '';
    // Australian addresses typically end "..., Suburb NSW POSTCODE, Australia"
    const parts = addr.split(',').map(s => s.trim());
    if (parts.length >= 2) {
      // Try second-to-last (suburb usually before "NSW POSTCODE")
      const candidate = parts[parts.length - 2] || '';
      // Strip leading state/postcode if present
      return candidate.replace(/\s+NSW\s+\d{4}.*$/i, '').trim() || candidate;
    }
    return addr;
  };

  // Escape CSV field
  const escape = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const rows = report.trips.map(t => [
    t.referenceNumber || '',
    fmtDate(t.completedAt),
    fmtTime(t.completedAt),
    t.vehiclePlate || '',
    extractSuburb(t.pickupAddress),
    extractSuburb(t.destinationAddress),
    (t.fareExclLevy || 0).toFixed(2),
    (t.levyAmount || 0).toFixed(2),
    (t.gst || 0).toFixed(2),
    (t.fareTotal || 0).toFixed(2),
    t.paymentStatus || '',
    t.levyStatus || '',
    t.seriesId || '',
    t.vehicleGroupId || '',
  ].map(escape).join(','));

  const csv = [headers.join(','), ...rows].join('\n');

  // Trigger browser download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `reservo-levy-${report.monthKey}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);

return { rowCount: rows.length };
}

// ─── Helper: time-until-deadline formatter for UI display ───
// Returns { overdue: bool, label: string } where label is "OVERDUE", "5min remaining",
// "3hr remaining", or "2d 4hr remaining" depending on time to deadline.
export function timeUntilDeadline(deadlineISO) {
  if (!deadlineISO) return null;
  const deadline = new Date(deadlineISO);
  const now = new Date();
  const diffMs = deadline - now;
  if (diffMs < 0) return { overdue: true, label: 'OVERDUE' };
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 1) return { overdue: false, label: `${Math.ceil(diffMs / 60000)}min remaining` };
  if (hours < 24) return { overdue: false, label: `${hours}hr remaining` };
  const days = Math.floor(hours / 24);
  return { overdue: false, label: `${days}d ${hours % 24}hr remaining` };
}